import { Tiers } from 'src/types/meta/whatsappTiers';
import { whatsappCountries } from '../../components/administration/whatsapp/pricing/whatsappCountries';
import translatedCountriesOptions from '../../components/common/translatedWhatsappCountries';
import { Contact } from '../../types/contact';
import { GetWhatsappPricing, PostWhatsappPricing, WhatsappCountry, WhatsappMonthlyFee } from '../../types/meta/whatsappPricing';
import { HttpClient } from '../common/httpClient';

type GetWhatsappPricingResponse = {
  readonly id: string;
  readonly country: { callingCode: number };
  readonly countryName: string;
  readonly breakout: string;
  readonly marketingPrice: number;
  readonly utilityPrice: number;
  readonly authenticationPrice: number;
  readonly authInternationalPrice: number;
  readonly servicePrice: number;
  readonly updateReason?: string;
  readonly isBlocked?: boolean;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly periodCategory?: string;
  readonly tiers?: Tiers;
  readonly tieredPricingEnabled?: boolean;
};

type WhatsappMonthlyFeeResponse = {
  readonly fee: number;
};

type WhatsappCountryResponse = {
  readonly dialCode: string;
  readonly countryName: string;
  readonly countryCode: string;
};

type WhatsappContactsWithoutPricing = {
  readonly phoneNumbers: number;
  readonly countries: string[];
};

export class WhatsappPricingApi {
  constructor(private readonly httpClient: HttpClient) { }

  private mapToGetWhatsappPricing(response: GetWhatsappPricingResponse): GetWhatsappPricing {
    const matchedCountry = whatsappCountries.find(
      country => country.dialCode === String(response.country?.callingCode)
    );

    const mapTiers = (
      category: 'UTILITY' | 'AUTHENTICATION' | 'AUTHENTICATION_INTERNATIONAL',
    ) => {
      const arr = response.tiers?.[category] || [];

      const sorted = [...arr].sort((a, b) => {
        if (a.name === 'Base') return -1;
        if (b.name === 'Base') return 1;
        return 0;
      });

      const tiers: { name: string; price: string }[] = [];

      sorted.forEach((entry, idx) => {
        tiers.push({
          name: entry.name === 'Base' ? 'Base' : `Tier ${idx}`,
          price: entry.price?.toFixed(4) || '0.0000',
        });
      });

      return tiers;
    };

    const tieredPricingEnabled = response.tiers && Object.keys(response.tiers).length > 0;

    return {
      id: response.id,
      country: {
        callingCode: response.country.callingCode,
      },
      countryName: matchedCountry?.name! || response.countryName,
      marketing: response.marketingPrice.toFixed(4),
      utility: response.utilityPrice.toFixed(4),
      authentication: response.authenticationPrice.toFixed(4),
      authInternational: response.authInternationalPrice?.toFixed(4),
      service: response.servicePrice.toFixed(4),
      startDate: response.startDate,
      endDate: response.endDate,
      updateReason: response.updateReason,
      isBlocked: response.isBlocked,
      periodCategory: response.periodCategory,
      tieredPricingEnabled,
      utilityTieredPricing: mapTiers('UTILITY'),
      authenticationTieredPricing: mapTiers('AUTHENTICATION'),
      authInternationalTieredPricing: mapTiers('AUTHENTICATION_INTERNATIONAL'),
    };
  }

  private mapToWhatsappMonthlyFee(response: WhatsappMonthlyFeeResponse): WhatsappMonthlyFee {
    return {
      fee: response.fee,
    };
  }

  private mapToWhatsappCountry(response: WhatsappCountryResponse): WhatsappCountry {
    const translationKey = translatedCountriesOptions[response.countryName]
    return {
      dialCode: response.dialCode,
      countryName: translationKey || response.countryName,
      countryCode: response.countryCode,
    };
  }

  private mapToWhatsappContactsWithoutPricing(response: WhatsappContactsWithoutPricing): WhatsappContactsWithoutPricing {
    return {
      phoneNumbers: response.phoneNumbers,
      countries: response.countries,
    };
  }

  getAll(businessAccountId: string): Promise<GetWhatsappPricing[]> {
    return this.httpClient.get<GetWhatsappPricingResponse[]>(`pricing/${businessAccountId}`)
      .then(data => (
        data.map(it => this.mapToGetWhatsappPricing(it))
      ));
  }

  getMonthlyFee(tenantId: string): Promise<WhatsappMonthlyFee> {
    return this.httpClient.get<WhatsappMonthlyFeeResponse>(`price-list/${tenantId}/monthly-fee`)
      .then(data => this.mapToWhatsappMonthlyFee(data));
  }

  getCountries(): Promise<WhatsappCountry[]> {
    return this.httpClient.get<WhatsappCountry[]>(`dial-code`)
      .then(data => (
        data.map(it => this.mapToWhatsappCountry(it))
      ));
  }

  async updateMonthlyFee(tenantId: string, fee: number): Promise<void> {
    await this.httpClient.put<WhatsappMonthlyFee>(`price-list/${tenantId}/monthly-fee`, { fee });
  }

  async add(businessAccountId: string, values: PostWhatsappPricing): Promise<void> {
    await this.httpClient.post<PostWhatsappPricing>(`pricing/${businessAccountId}/price`, values);
  }

  async update(businessAccountId: string, id: string, item: PostWhatsappPricing): Promise<void> {
    await this.httpClient.put<PostWhatsappPricing>(`pricing/${businessAccountId}/price/${id}`, item);
  }

  async delete(businessAccountId: string, id: string): Promise<void> {
    await this.httpClient.delete<GetWhatsappPricing>(`pricing/${businessAccountId}/price/${id}`);
  }

  getContactsWithoutPricing(businessAccountId: number, values: Contact[]): Promise<WhatsappContactsWithoutPricing> {
    return this.httpClient.post<WhatsappContactsWithoutPricing>(`pricing/${businessAccountId}/verification`, { contacts: values })
      .then(data => this.mapToWhatsappContactsWithoutPricing(data));
  }

  async blockCountry(businessAccountId: string, country: number): Promise<void> {
    await this.httpClient.post<PostWhatsappPricing>(`pricing/${businessAccountId}/country/banned/${country}`, {});
  }

  async unblockCountry(businessAccountId: string, country: number): Promise<void> {
    await this.httpClient.delete<GetWhatsappPricing>(`pricing/${businessAccountId}/country/banned/${country}`);
  }
}
